import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, Eye, FileText, Clock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { listBriefingReports, getBriefingReport } from '../lib/api';
import { format } from 'date-fns';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'MMM d, yyyy · h:mm a'); } catch { return iso; }
};

/**
 * BriefingReportsDrawer
 *
 * Modal that lists all finalized "Concierge Briefing" reports (one per End Shift)
 * and lets the user view any past report read-only. Reports are append-only and
 * cannot be edited — this is the audit/presentation surface.
 */
export const BriefingReportsDrawer = ({ open, onOpenChange }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // selected report (full payload)
  const [activeLoading, setActiveLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setActive(null);
    setLoading(true);
    listBriefingReports()
      .then((res) => setReports(res.data || []))
      .catch(() => toast.error('Could not load past briefings'))
      .finally(() => setLoading(false));
  }, [open]);

  const openReport = async (r) => {
    setActiveLoading(true);
    try {
      const res = await getBriefingReport(r.id);
      setActive(res.data);
    } catch {
      toast.error('Could not open that briefing');
    } finally {
      setActiveLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh]" data-testid="briefing-reports-drawer">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#3F617E]" />
            {active ? 'Concierge Briefing — closed report' : 'Past Concierge Briefings'}
          </DialogTitle>
          <DialogDescription>
            {active
              ? 'Read-only. Each briefing is locked at End Shift — safe to share for audit or review.'
              : 'Each completed shift produces one finalized briefing. Open any to review or present.'}
          </DialogDescription>
        </DialogHeader>

        {!active ? (
          <ScrollArea className="max-h-[65vh] pr-2">
            {loading ? (
              <div className="py-12 text-center text-stone-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
            ) : reports.length === 0 ? (
              <div className="py-10 text-center">
                <Clock className="h-8 w-8 text-stone-300 mx-auto mb-2" />
                <p className="text-sm text-stone-600 font-medium">No closed briefings yet</p>
                <p className="text-xs text-stone-400 mt-1">A briefing is locked the moment you End Shift.</p>
              </div>
            ) : (
              <div className="space-y-2 py-2">
                {reports.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-stone-100 hover:border-stone-200"
                    data-testid={`briefing-report-row-${r.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-stone-900 flex items-center gap-2">
                        <span>{r.date}</span>
                        <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">Closed</Badge>
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5">
                        Locked {formatDate(r.closed_at)} · {r.updated_tasks_count || 0} carry-over{(r.updated_tasks_count || 0) === 1 ? '' : 's'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => openReport(r)}
                      data-testid={`view-report-${r.id}`}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        ) : (
          <ScrollArea className="max-h-[65vh] pr-2">
            <div className="space-y-4 py-2">
              <Button size="sm" variant="ghost" onClick={() => setActive(null)} data-testid="briefing-back">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to list
              </Button>
              {activeLoading ? (
                <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-stone-400" /></div>
              ) : (
                <BriefingPayloadView report={active} />
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, hint, items, renderItem, empty }) => (
  <div className="space-y-2">
    <div>
      <h4 className="text-sm font-semibold text-stone-800">{title}</h4>
      {hint && <p className="text-xs text-stone-500">{hint}</p>}
    </div>
    {items && items.length > 0 ? (
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={it.id || i} className="p-2.5 bg-stone-50 rounded-md border border-stone-100">
            {renderItem(it)}
          </div>
        ))}
      </div>
    ) : (
      <p className="text-xs text-stone-400 italic">{empty}</p>
    )}
  </div>
);

const BriefingPayloadView = ({ report }) => {
  const p = report?.payload || {};
  const s = p.sections || {};
  const ep = p.experienceProtection || {};

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="p-4 bg-[#3F617E]/5 rounded-lg border border-[#3F617E]/15">
        <p className="text-xs text-[#3F617E] font-semibold tracking-wide uppercase">Concierge Briefing</p>
        <h3 className="text-lg font-heading text-stone-900 mt-1">
          {p.header?.propertyName || 'All properties'} — {p.header?.periodLabel || 'Today'}
        </h3>
        <p className="text-xs text-stone-500 mt-1">Locked {formatDate(report?.closed_at)}</p>
      </div>

      {/* Morning brief */}
      {p.morningBrief && (
        <div className="p-3 bg-stone-50 rounded-md border border-stone-100">
          <h4 className="text-sm font-semibold text-stone-800 mb-1">Morning brief</h4>
          <ul className="text-xs text-stone-700 space-y-1">
            <li><strong>Closed:</strong> {p.morningBrief.closed ?? '—'}</li>
            <li><strong>Open:</strong> {p.morningBrief.open ?? '—'}</li>
            <li><strong>Urgent:</strong> {p.morningBrief.handleNow ?? '—'}</li>
          </ul>
        </div>
      )}

      <Section
        title="Handle Now"
        hint="Time-sensitive items that protected access, arrival, comfort, or trust."
        items={s.handleNow}
        empty="No urgent items at close."
        renderItem={(t) => (
          <div>
            <p className="text-xs font-medium text-stone-800">{t.summary}</p>
            <p className="text-[10px] text-stone-500 mt-0.5">{t.category?.replace(/_/g, ' ')} · {t.guest_name_text || 'Guest not linked'}</p>
          </div>
        )}
      />

      <Section
        title="Due Today"
        hint="Close-by-end-of-day items."
        items={s.dueToday}
        empty="Today was clear."
        renderItem={(t) => (
          <div>
            <p className="text-xs font-medium text-stone-800">{t.summary}</p>
            <p className="text-[10px] text-stone-500 mt-0.5">{t.category?.replace(/_/g, ' ')}</p>
          </div>
        )}
      />

      <Section
        title="Waiting On"
        hint="Paused — waiting on guest, vendor, or team."
        items={s.waitingOn}
        empty="Nothing was waiting at close."
        renderItem={(t) => (
          <div>
            <p className="text-xs font-medium text-stone-800">{t.summary}</p>
            <p className="text-[10px] text-stone-500 mt-0.5">Waiting on {t.waiting_on?.replace(/_/g, ' ') || '—'}</p>
          </div>
        )}
      />

      <Section
        title="Carry-Over"
        hint="Items that crossed over the shift with a reason and next touch."
        items={s.carryOver}
        empty="Clean handoff — nothing carried over."
        renderItem={(t) => (
          <div>
            <p className="text-xs font-medium text-stone-800">{t.summary}</p>
            <p className="text-[10px] text-stone-500 mt-0.5">Reason: {t.carry_over_reason?.replace(/_/g, ' ') || 'Not recorded'} · Next: {t.next_touch_at ? formatDate(t.next_touch_at) : 'Not set'}</p>
          </div>
        )}
      />

      <Section
        title="Manager Approvals"
        hint="Decisions that needed a sign-off — GM, Front Desk Manager, or Manager on Duty."
        items={ep.owner_approvals || ep.ownerApprovals}
        empty="No approvals queued at close."
        renderItem={(t) => (
          <div>
            <p className="text-xs font-medium text-stone-800">{t.owner_decision_summary || t.summary}</p>
            {t.dueAt && <p className="text-[10px] text-stone-500 mt-0.5">By {formatDate(t.dueAt)}</p>}
          </div>
        )}
      />

      <Section
        title="What We Handled"
        hint="Closed items at the time of shift end."
        items={s.whatWeHandled}
        empty="Nothing closed during this shift."
        renderItem={(t) => (
          <div>
            <p className="text-xs font-medium text-stone-800">{t.summary}</p>
            <p className="text-[10px] text-stone-500 mt-0.5">{t.category?.replace(/_/g, ' ')} · closed {t.closed_at ? formatDate(t.closed_at) : ''}</p>
          </div>
        )}
      />
    </div>
  );
};
